// @ts-strict-ignore
import React, { useEffect, useCallback, useLayoutEffect } from 'react'
import { useNavigation } from '@react-navigation/native'
import { View } from 'react-native'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import { useTheme } from 'styled-components/native'
import { NativeStackScreenProps } from '@react-navigation/native-stack'
import ServiceAuthenticationError from 'APP/Components/ServiceAuthenticationError'
import PatientActions from 'APP/Redux/PatientRedux'
import { getServiceGroupIcon } from 'APP/Lib/CtaInfoHelpers'
import DialogueWebView from 'APP/Components/DialogueWebView'
import useServiceData from 'APP/Hooks/useServiceData'
import { ResponsiveView } from 'APP/Converse/Layout'
import ActivityIndicator from 'APP/Components/ActivityIndicator'
import Analytics from 'APP/Services/Analytics'
import { selectPharmacies } from 'APP/Store/Pharmacies/selectors'
import { getPharmacyProvince } from 'APP/Lib/PillwayHelpers'
import { replacePlaceholderURLTextWithConfig } from 'APP/Lib/ContentHelpers'
import Config from 'APP/Config'

type PillwayPortalScreenProps = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  'pillwayPortalScreen'
>

const PillwayPortalScreen = ({ route }: PillwayPortalScreenProps) => {
  const dispatch = useDispatch()
  const navigation = useNavigation()
  const theme = useTheme()

  const {
    navigationUrl,
    serviceGroup,
    // Note: Properties below derive from deeplink entry points, set through: DeeplinkActions.setDeferredDeeplink
    id: serviceGroupId,
    healthResource: serviceId,
    '~referring_link': referringLink,
  } = route.params

  const {
    serviceGroup: serviceGroupData,
    service: serviceData,
    loading: serviceDataLoading,
    error: serviceDataError,
  } = useServiceData(serviceGroupId, serviceId)

  const pillwayUrl = navigationUrl || serviceData?.navigationUrl
  const serviceGroupInfo = serviceGroup || serviceGroupData

  const serviceGroupNotLoaded = !!(
    !navigationUrl &&
    serviceGroupId &&
    !serviceGroupData &&
    !serviceDataError
  )

  const requestPillwayAuthentication = useCallback(() => {
    dispatch(PatientActions.pillwayAuthenticationRequest())
  }, [dispatch])

  useEffect(() => {
    requestPillwayAuthentication()
  }, [requestPillwayAuthentication])

  const screenIconOverride = getServiceGroupIcon(serviceGroupInfo?.screenIconOverride)
  const { pillway } = useSelector((state) => state.patient)
  const { running, accessToken, error } = pillway || {}

  useLayoutEffect(() => {
    const handleNavigate = () => {
      navigation.goBack()
    }
    navigation.setOptions({
      rightButtonIcon: 'close',
      rightButtonIconVariant: 'material',
      rightButtonOnPress: handleNavigate,
    })
  }, [navigation, serviceGroupInfo?.id])

  const pharmacies = useSelector(selectPharmacies)

  // Loading state
  if (!pillway || running || serviceDataLoading || serviceGroupNotLoaded) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }} testID="LOADING">
        <ActivityIndicator
          color={theme.colors.secondary}
          size={'large'}
          testID="LOADING_INDICATOR"
        />
      </View>
    )
  }

  // Error state, show authentication error component
  if (!accessToken || !pillwayUrl || error) {
    return (
      <ServiceAuthenticationError
        screenIconOverride={screenIconOverride}
        serviceGroup={serviceGroup}
        onRetry={requestPillwayAuthentication}
      />
    )
  }

  // Modify navigation URL from contentful with Pillway URL from config.
  const url = replacePlaceholderURLTextWithConfig(pillwayUrl)

  // Reject any destination that doesn't point at our configured Pillway domain (e.g. an
  // attacker-supplied navigationUrl from a deep link) before the access token is ever attached.
  const pillwayHostname = Config.PILLWAY_DOMAIN ? new URL(Config.PILLWAY_DOMAIN).hostname : null
  if (url === null || pillwayHostname === null || url?.hostname !== pillwayHostname) {
    return (
      <ServiceAuthenticationError
        screenIconOverride={screenIconOverride}
        serviceGroup={serviceGroup}
        onRetry={requestPillwayAuthentication}
      />
    )
  }

  // extract all query params from the referring link if it exists, and append them to the URL
  if (referringLink) {
    try {
      const referringUrl = new URL(referringLink)
      const searchParams = referringUrl.searchParams || new URLSearchParams()
      searchParams.forEach((value, key) => {
        url.searchParams.set(key, value)
      })
    } catch (_e) {
      // In case of invalid referringLink, do nothing
    }
  }

  // check if UTM params exist in the Analytics entity
  const utmEntities = Analytics.getEntities(['utm_context']) || []
  const utmParams = utmEntities?.[0]?.data ?? {}
  if (utmParams) {
    Object.entries(utmParams).forEach(([key, value]) => {
      if (value) {
        url.searchParams.set(key, value)
      }
    })
  }

  url.searchParams.set('access_token', accessToken)

  const existingHash = url.hash.replace(/^#/, '')
  const [hashPath, existingHashQuery] = existingHash.split('?')
  const hashParams = new URLSearchParams(existingHashQuery)

  const pharmacyProvince = getPharmacyProvince(pharmacies?.[0])
  if (pharmacyProvince) hashParams.set('loc', pharmacyProvince)

  url.hash = hashPath ? `${hashPath}?${hashParams}` : `${hashParams}`

  const uriWithAccessToken = url.toString()

  // Render requested URL
  return (
    <ResponsiveView
      style={{ flex: 1, paddingBottom: theme.insets.bottom }}
      testID="PILLWAY_PORTAL_SCREEN"
    >
      <DialogueWebView
        uri={uriWithAccessToken}
        userSelect="none"
        incognito={true}
        originWhitelist={['https://', 'about:']}
      />
    </ResponsiveView>
  )
}

export default PillwayPortalScreen
